import React from 'react';
import clsx from 'clsx';
import { useResizeDetector } from 'react-resize-detector';
import { Checkbox, List, ListItemAvatar, ListItemIcon, ListItemText, styled } from '@mui/material';
import ListItem, { type ListItemProps } from '@mui/material/ListItem';

import { type FiltersType } from './hooks/useFilters';
import { type SelectionProps, type ItemSelectionEvent } from './hooks/useSelection';
import { type ViewItemButtonProps } from './page-layouts/view/ViewPage';
import MultiButton, { type MultiButtonOptionType } from './buttons/MultiButton';
import { getItemId } from './Utils';

export interface ItemLayoutProps {
	/** The component width */
	width: number;
	/** The settings instance */
	settings: any;
	/** The item model */
	model: any;
	/** If true the item can be selected */
	isSelecting?: boolean;
	/** The button props */
	buttonProps: ViewItemButtonProps;
	/** The function used to update the filters */
	changeFilter?: FiltersType['handleFilterChange'];
}

interface GridListItemContextType {
	/** The settings instance */
	settings: any;
	/** The item model to show in the grid */
	model: any;
	/** Object containing the item container filters */
	filters?: FiltersType['filters'];
	/** The function used to refresh the items container */
	updateRefresh: VoidFunction;
	/** If true the item can be selected */
	isSelecting?: boolean;
	/** If true the item is selected */
	isSelected?: boolean;
}

interface GridListItemProps {
	/** The grid list item content */
	children: React.ReactNode;
	/** The grid list item avatar */
	avatar?: React.ReactNode;
	/** The grid list item buttons */
	buttons?: MultiButtonOptionType<ViewItemButtonProps>[];
	/** The grid list item secondary action, if no buttons */
	secondaryAction?: React.ReactNode;
	/** Defines the align-items style property */
	alignItems?: ListItemProps['alignItems'];
}

export interface GridListProps {
	/** The component css class */
	className?: string;
	/** The settings instance */
	settings: any;
	/** (Optional) ID field key */
	idField?: string;
	/** List of items shown in the grid */
	items: any[];
	/** The component used to render the grid's items */
	itemLayout: React.FC<ItemLayoutProps>;
	/** Object containing the item container filters props */
	filterProps?: FiltersType;
	/** The function used to refresh the items container */
	updateRefresh: VoidFunction;
	/** The selection props */
	selectionProps?: SelectionProps;
}

const GridListItemContext = React.createContext<GridListItemContextType>({
	settings: undefined,
	model: undefined,
	updateRefresh: () => {}
});

const StyledListItem = styled(ListItem)(({ theme }) => ({
	'& > .MuiListItemSecondaryAction-root': {
		top: 4,
		marginTop: 8,
		transform: 'none'
	}
}));

export function GridListItem(props: GridListItemProps) {
	const { settings, model, filters, updateRefresh, isSelecting, isSelected } = React.useContext(GridListItemContext);

	const buttonProps: ViewItemButtonProps = { settings, model, filters, updateRefresh };

	function handleSelectionCheckboxClick(item) {
		const event: ItemSelectionEvent = new CustomEvent('handleItemSelection', {
			detail: { newSelectedItem: item }
		});
		document.dispatchEvent(event);
	}

	return (
		<StyledListItem
			className={clsx('pl-8 last:border-b-0', !isSelecting && 'sm:pl-12')}
			alignItems={props.alignItems || 'flex-start'}
			divider
			secondaryAction={
				props.buttons ? (
					<MultiButton buttonProps={buttonProps} items={props.buttons} edge="end" />
				) : (
					props.secondaryAction
				)
			}
		>
			{isSelecting && (
				<ListItemIcon className="min-w-48">
					<Checkbox checked={isSelected} onChange={() => handleSelectionCheckboxClick(model)} />
				</ListItemIcon>
			)}
			{props.avatar && <ListItemAvatar>{props.avatar}</ListItemAvatar>}
			<ListItemText>{props.children}</ListItemText>
		</StyledListItem>
	);
}

export default function GridList(props: GridListProps) {
	const { ref, width } = useResizeDetector({ handleHeight: false });

	const isSelecting = !!props.selectionProps;

	const compareSelectedItems = (selectedItem, item) =>
		getItemId(selectedItem, props.idField, props.settings) === getItemId(item, props.idField, props.settings);

	return (
		<List ref={ref} className={props.className} dense disablePadding>
			{props.items.map((item, i) => {
				const isSelected = props.selectionProps?.selectedItems
					? !!props.selectionProps?.selectedItems.find(selectedItem =>
							compareSelectedItems(selectedItem, item)
						)
					: false;

				const listItemProps: GridListItemContextType = {
					settings: props.settings,
					model: item,
					filters: props.filterProps?.filters,
					updateRefresh: props.updateRefresh,
					isSelecting,
					isSelected
				};

				if (!width) return <React.Fragment key={i}></React.Fragment>;

				return (
					<GridListItemContext.Provider key={i} value={listItemProps}>
						<props.itemLayout
							width={width}
							settings={props.settings}
							model={item}
							isSelecting={isSelecting}
							buttonProps={listItemProps}
							changeFilter={(name, operator, value) => {
								props.filterProps?.handleFilterChange(name, operator, value);
								props.filterProps?.handleFiltersApply();
							}}
						/>
					</GridListItemContext.Provider>
				);
			})}
		</List>
	);
}
